import CategoryData from "@constants/CategoricalData.json"

export type Category = { id: string; title: string }

export interface FindBestOptions {
  debug?: boolean
  weights?: Partial<Weights>
}

export interface MatchDebug {
  deviceBrand: null | string
  reasons: Array<[string, number]>
  specificity: number
  titleBigrams: string[]
  titleBrand: null | string
  titleNumbers: string[]
  titleTokens: string[]
}

export interface MatchResult {
  debug?: MatchDebug | null
  match: Category | null
  score: number
}

export interface Weights {
  brandAffinity: number // mild bonus for matching brand
  brandMismatch: number // device brand != category brand
  genericPenalty: number // generic / catch-all collections
  number: number // exact numeric match (eg "16")
  otherNumberPenalty: number // category has numbers not in device
  partial: number // light startsWith overlap
  phrase: number // bigram phrase overlap, (eg "pro max")
  token: number // exact token overlap
}

// Adjust as needed
const DEFAULT_WEIGHTS: Weights = {
  brandAffinity: 1,
  brandMismatch: -6,
  genericPenalty: -2,
  number: 3,
  otherNumberPenalty: -1,
  partial: 0.5,
  phrase: 3,
  token: 2,
}

// Stopwords removed from category titles to avoid noisy overlap
const TITLE_STOPWORDS = new Set([
  "accessories",
  "all",
  "and",
  "bag",
  "bags",
  "cables",
  "case",
  "cases",
  "collection",
  "collections",
  "edition",
  "gear",
  "headphones",
  "laptop",
  "laptops",
  "limited",
  "my",
  "phone",
  "phones",
  "power",
  "product",
  "products",
  "recommended",
  "series",
  "sleeves",
  "tablet",
  "tablets",
  "watch",
  "watches",
  "wholesale",
  "with",
])

// Generic/low-signal phrases to penalize
const GENERIC_HINTS = [
  "all products",
  "all iPhones",
  "limited edition",
  "my collection",
  "recommended products",
  "wholesale",
]

// Ensures that "iPhone" maps to Apple, "Galaxy"/"Tab" to Samsung, etc
function buildBrandAliases(): Map<string, string> {
  const map = new Map<string, string>()

  // 1) Normalized brand names from data
  for (const b of CategoryData.brands) {
    const n = normalize(b)
    if (n.length > 0) {
      map.set(n, b)
    }
  }

  // 2) Pragmatic aliases
  const A = (alias: string, brand: string): void => {
    const key = normalize(alias)
    if (key.length > 0) {
      map.set(key, brand)
    }
  }

  const brandName = (name: string): string => {
    // Prefer canonical brand name from JSON if it exists
    const hit = CategoryData.brands.find(
      (b) => normalize(b) === normalize(name)
    )
    if (hit) {
      return hit
    }
    return name
  }

  // Apple
  A("iphone", brandName("Apple"))
  A("ipad", brandName("Apple"))
  A("macbook", brandName("Apple"))
  A("airpods", brandName("Apple"))
  A("airpod", brandName("Apple"))
  A("airtag", brandName("Apple"))
  A("apple watch", brandName("Apple"))
  A("watch ultra", brandName("Apple"))

  // Samsung
  A("galaxy", brandName("Samsung"))
  A("note", brandName("Samsung"))
  A("tab", brandName("Samsung"))
  A("buds", brandName("Samsung"))
  A("z flip", brandName("Samsung"))
  A("z fold", brandName("Samsung"))

  // Google
  A("pixel", brandName("Google"))

  // Microsoft
  A("surface", brandName("Microsoft"))

  // Others
  A("latitude", brandName("Dell"))

  A("hp", brandName("HP"))

  A("mate", brandName("Huawei"))
  A("p50", brandName("Huawei"))
  A("p60", brandName("Huawei"))
  A("p70", brandName("Huawei"))

  A("chromebook", brandName("Lenovo"))
  A("lenovo", brandName("Lenovo"))

  A("lg", brandName("LG"))

  A("moto", brandName("Motorola"))
  A("motorola", brandName("Motorola"))

  A("oneplus", brandName("OnePlus"))

  A("revvl", brandName("T-Mobile"))

  A("mi", brandName("Xiaomi"))
  A("redmi", brandName("Xiaomi"))
  A("xiaomi", brandName("Xiaomi"))

  return map
}

const BRAND_ALIASES = buildBrandAliases()

export function findBestCategory(
  device: string,
  categories: Category[],
  opts: FindBestOptions = {}
): MatchResult {
  const W: Weights = { ...DEFAULT_WEIGHTS, ...(opts.weights ?? {}) }

  const deviceTokensRaw = tokenize(device)
  const deviceTokens = new Set(deviceTokensRaw)
  const deviceBigrams = new Set(ngrams(deviceTokensRaw, 2))
  const deviceNumbers = new Set(getNumbers(deviceTokensRaw))
  const deviceBrand = detectBrand(device)

  let best: MatchResult = { debug: null, match: null, score: -Infinity }

  for (const cat of categories) {
    const titleNorm = normalize(cat.title)
    const titleTokens = tokenize(cat.title, { dropStopwords: true })
    const titleTokenSet = new Set(titleTokens)
    const titleBigrams = new Set(ngrams(titleTokens, 2))
    const titleNumbers = new Set(getNumbers(titleTokens))
    const titleBrand = detectBrand(cat.title)

    let score = 0
    const reasons: Array<[string, number]> = []

    // 1) Brand logic
    if (deviceBrand !== null && titleBrand !== null) {
      if (deviceBrand === titleBrand) {
        score += W.brandAffinity
        reasons.push(["brandAffinity", W.brandAffinity])
      } else {
        score += W.brandMismatch
        reasons.push(["brandMismatch", W.brandMismatch])
      }
    }

    // 2) Exact token overlaps (after stopword drop on titles)
    for (const tok of titleTokenSet) {
      if (deviceTokens.has(tok)) {
        score += W.token
        reasons.push([`token:${tok}`, W.token])
        continue
      }

      // Light partial (prefix) overlap for longer tokens (e.g., "ultra" vs "ultrawide" not awarded)
      const hasLoose = Array.from(deviceTokens).some((dt) => {
        if (dt.length < 4 && tok.length < 4) {
          return false
        }
        if (tok.startsWith(dt) || dt.startsWith(tok)) {
          return true
        }
        return false
      })

      if (hasLoose) {
        score += W.partial
        reasons.push([`partial:${tok}`, W.partial])
      }
    }

    // 3) Phrase (bigram) overlaps like "pro max"
    for (const bi of titleBigrams) {
      if (deviceBigrams.has(bi)) {
        score += W.phrase
        reasons.push([`phrase:${bi}`, W.phrase])
      }
    }

    // 4) Number emphasis & mismatches
    if (titleNumbers.size > 0) {
      for (const n of titleNumbers) {
        if (deviceNumbers.has(n)) {
          score += W.number
          reasons.push([`number:${n}`, W.number])
        } else if (deviceNumbers.size > 0) {
          score += W.otherNumberPenalty
          reasons.push([`otherNumber:${n}`, W.otherNumberPenalty])
        }
      }
    }

    // 5) Generic penalties
    for (const g of GENERIC_HINTS) {
      if (titleNorm.includes(g)) {
        score += W.genericPenalty
        reasons.push([`generic:${g}`, W.genericPenalty])
      }
    }

    // Tiebreakers: prefer "more specific" titles
    const specificity = titleTokens.length
    const tieBreaker = specificity * 1e-3 + titleNorm.length * 1e-5
    const finalScore = score + tieBreaker

    if (finalScore > best.score) {
      best = {
        debug: opts.debug
          ? {
              deviceBrand,
              reasons,
              specificity,
              titleBigrams: Array.from(titleBigrams),
              titleBrand,
              titleNumbers: Array.from(titleNumbers),
              titleTokens: Array.from(titleTokenSet),
            }
          : null,
        match: cat,
        score: Number(finalScore.toFixed(3)),
      }
    }
  }

  if (best.score === -Infinity) {
    return { debug: opts.debug ? null : undefined, match: null, score: 0 }
  }

  return best
}

// Return top N matches with scores (stable sort)
export function topMatches(
  device: string,
  categories: Category[],
  n = 3,
  opts: FindBestOptions = {}
): Array<{ category: Category; score: number }> {
  const scored = categories.map((c) => {
    const { score } = findBestCategory(device, [c], opts)
    return { category: c, score }
  })
  scored.sort((a, b) => b.score - a.score)
  return scored.slice(0, Math.max(0, n))
}

function detectBrand(str: string): null | string {
  const t = normalize(str)

  // Prefer longer aliases first to avoid "tab" capturing "z tab" style overlaps
  const aliases = Array.from(BRAND_ALIASES.keys()).sort(
    (a, b) => b.length - a.length
  )
  for (const alias of aliases) {
    if (t.includes(alias)) {
      const brand = BRAND_ALIASES.get(alias) ?? null
      if (brand !== null) {
        return brand
      }
    }
  }
  return null
}

function getNumbers(tokens: string[]): string[] {
  // Allow integers only for scoring; decimals are split earlier
  return tokens.filter((t) => /^\d+$/.test(t))
}

function ngrams(tokens: string[], n: number): string[] {
  const out: string[] = []
  for (let i = 0; i <= tokens.length - n; i += 1) {
    out.push(tokens.slice(i, i + n).join(" "))
  }
  return out
}

function normalize(str: string): string {
  return (
    str
      .toLowerCase()
      // Normalize curly quotes & inch symbols
      .replace(/[“”„‟″]/g, '"')
      .replace(/[’‘‚‛]/g, "'")
      .replace(/[\u00A0\u2000-\u200D\u202F\u205F\u3000]/g, " ") // Various non-breaking spaces
      // Keep letters, numbers, plus, and quotes for inch marks; collapse others to space
      .replace(/[^a-z0-9+"']+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
  )
}

function singularize(word: string): string {
  if (word.endsWith("ies")) {
    return word.slice(0, -3) + "y"
  }
  if (word.endsWith("ses")) {
    return word.slice(0, -2)
  }
  if (word.endsWith("s") && word.length > 3) {
    return word.slice(0, -1)
  }
  return word
}

function tokenize(
  str: string,
  { dropStopwords = false }: { dropStopwords?: boolean } = {}
): string[] {
  const norm = normalize(str)
  if (norm.length === 0) {
    return []
  }
  let tokens = norm.split(" ").filter(Boolean)

  // Transform inch-like tokens: `12.9"` => ["12", "9"] and `13"` => ["13"]
  tokens = tokens.flatMap((t) => {
    const inch = t.replace(/"$/, "")
    if (/"$/.test(t) && /\d/.test(inch)) {
      const parts = inch.split(".")
      if (parts.length === 2) {
        return parts // "12.9" -> ["12","9"]
      }
      return [inch]
    }
    return [t]
  })

  tokens = tokens.map(singularize)

  if (dropStopwords) {
    return tokens.filter((t) => !TITLE_STOPWORDS.has(t))
  }
  return tokens
}
